// The sign-in gate, shared by every page.
//
// WHY THIS FILE EXISTS. It used to live inside App.tsx, which was fine while App was the
// only page. Adding /dashboard broke that immediately and silently: Dashboard called
// `authHeaders()` without anything having called `initAuth()` first, so `authConfig` was
// null, `getApiToken()` took its `!authConfig?.authRequired` early return — the branch
// meant for local dev with auth off — and returned null. No Authorization header was
// sent, and every panel came back 401.
//
// That is the worst shape a bug can have here: the failure was in the CALLER, three
// files from the symptom, and it looked exactly like a backend permission problem. The
// fix is not "call initAuth in Dashboard too" — that is the same mistake waiting for a
// third page. The gate now wraps whatever main.tsx renders, so a page cannot be added
// outside it, and client.test.mjs asserts that every page is inside it.

import { useEffect, useState } from "react";
import { authRequired, clientName, consumeSignedOutFlag, friendlyAuthError, initAuth, signIn } from "./auth/msal";

type Phase = "loading" | "signin" | "ready";

export interface Account {
  name?: string;
  username?: string;
}

function SignIn({ error, signedOut }: { error: string | null; signedOut: boolean }) {
  return (
    <main className="signin">
      <div className="signin-card">
        <div className="signin-mark" aria-hidden="true">
          C
        </div>
        <h1>{clientName()} HR Assistant</h1>
        {/* The sign-out confirmation replaces the usual strapline rather than stacking
            on top of it. Coming back from Microsoft, "You're signed out" is the one
            thing the user needs to see, and the product pitch underneath it reads as
            though the sign-out did not take. */}
        {signedOut ? (
          <p className="signin-note">You're signed out. Sign in again to pick up where you left off.</p>
        ) : (
          <p>Answers drawn from your own HR policies, and drafts you can edit before anyone sees them.</p>
        )}
        {error && <div className="signin-error">{error}</div>}
        <button type="button" className="btn primary" onClick={signIn}>
          Sign in with Microsoft
        </button>
      </div>
    </main>
  );
}

/** Resolve auth, then render `children(account)`.
 *
 *  `children` is a function rather than an element so a page receives the account
 *  without a context or a second lookup — and, more usefully, so it CANNOT render
 *  before auth has resolved. An element child would be constructed eagerly, which is
 *  how the 401 happened: the page mounted and fetched while authConfig was still null. */
export default function AuthGate({ children }: { children: (account: Account | null) => React.ReactNode }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [authError, setAuthError] = useState<string | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  // Read ONCE on mount, before initAuth() can redirect again, and cleared by the read
  // itself so a later reload shows the ordinary screen.
  const [signedOut] = useState(consumeSignedOutFlag);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const acct = await initAuth();
        if (cancelled) return;
        if (!authRequired()) {
          // Local development with REQUIRE_AUTH off: no registration needed, and no
          // sign-in screen to click past on every reload.
          setPhase("ready");
          return;
        }
        if (acct) {
          setAccount({ name: acct.name, username: acct.username });
          setPhase("ready");
        } else {
          setPhase("signin");
        }
      } catch (err) {
        if (cancelled) return;
        setAuthError(friendlyAuthError(err instanceof Error ? err.message : ""));
        setPhase("signin");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (phase === "loading") {
    return (
      <div className="signin">
        <div className="signin-card" role="status">
          <div className="signin-mark" aria-hidden="true">
            C
          </div>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (phase === "signin") return <SignIn error={authError} signedOut={signedOut} />;
  return <>{children(account)}</>;
}
